import * as React from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Settings, User, UserCog, Sun, Moon, Eye, EyeOff } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu'
import LanguageSwitcher from '../LanguageSwitcher'
import { ImpersonationMenuContent } from '@/components/ui/ImpersonationDropdown'

export interface TopbarUserMenuProps {
  user: {
    name: string
    email: string
    avatar?: string
    role?: string
  }
  showAdminMenuItems: boolean
  canImpersonate: boolean
  signOut: (() => void) | undefined
  t: (key: string) => string
  wacklerLens?: {
    active: boolean
    onToggle: () => void
  }
}

export function TopbarUserMenu({
  user,
  showAdminMenuItems,
  canImpersonate,
  signOut,
  t,
  wacklerLens,
}: TopbarUserMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="flex items-center space-x-2 min-w-0 max-w-dropdown shrink-0 rounded-full sm:rounded-md h-9"
          title={t('header.account')}
          aria-label={t('header.account')}
          data-testid="topbar-settings-utilities-trigger"
        >
          {user.avatar ? (
            <img src={user.avatar} alt={user.name} className="h-7 w-7 rounded-full flex-shrink-0" />
          ) : (
            <User className="h-6 w-6 flex-shrink-0" strokeWidth={2} />
          )}
          <span className="hidden xl:inline truncate">{user.name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" data-testid="topbar-settings-utilities-menu">
        <div className="px-2 py-1.5">
          <p className="text-sm font-medium">{user.name}</p>
          <p className="text-xs text-muted-foreground">{user.email}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings/profile" className="flex items-center gap-2 cursor-pointer">
            <User className="h-4 w-4" />
            {t('header.myProfile')}
          </Link>
        </DropdownMenuItem>
        {showAdminMenuItems && (
          <DropdownMenuItem asChild>
            <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
              <Settings className="h-4 w-4" />
              {t('header.preferences')}
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5 flex items-center justify-between">
          <span className="text-sm font-medium">{t('header.appearance')}</span>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-accent/10 transition-colors"
            onClick={() => {
              const html = document.documentElement
              const isDark = html.classList.contains('dark')
              html.classList.toggle('dark', !isDark)
              try { localStorage.setItem('theme', isDark ? 'light' : 'dark') } catch { /* storage unavailable */ }
            }}
          >
            <Sun className="h-3.5 w-3.5 hidden dark:inline-block" />
            <Moon className="h-3.5 w-3.5 inline-block dark:hidden" />
            <span className="hidden dark:inline">{t('header.lightMode')}</span>
            <span className="inline dark:hidden">{t('header.darkMode')}</span>
          </button>
        </div>
        <div className="px-2 py-1.5 flex items-center justify-between">
          <span className="text-sm font-medium">{t('header.language')}</span>
          <LanguageSwitcher />
        </div>
        {wacklerLens && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => wacklerLens.onToggle()}
            >
              {wacklerLens.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              <span>{wacklerLens.active ? t('wacklerLens.on.label') : t('wacklerLens.off.label')}</span>
            </DropdownMenuItem>
          </>
        )}
        {canImpersonate && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="flex items-center gap-2">
                <UserCog className="h-4 w-4 shrink-0" />
                {t('header.impersonation')}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-64 max-h-dropdown overflow-y-auto overscroll-contain">
                <ImpersonationMenuContent />
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-danger" onClick={() => signOut?.()}>Sign Out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
